import { useTranslation } from 'react-i18next';
import { formatDistance, formatDuration } from '../../utils/distance';
import { ApproxMarker } from './ApproxMarker';
import styles from '../../components/domain/FormModal.module.css';

// Material "route" and "schedule" glyphs (no icon dependency, see TruckIcon).
const ROUTE_PATH =
  'M19 15.18V7c0-2.21-1.79-4-4-4s-4 1.79-4 4v10c0 1.1-.9 2-2 2s-2-.9-2-2V8.82C8.16 8.4 9 7.3 9 6c0-1.66-1.34-3-3-3S3 4.34 3 6c0 1.3.84 2.4 2 2.82V17c0 2.21 1.79 4 4 4s4-1.79 4-4V7c0-1.1.9-2 2-2s2 .9 2 2v8.18c-1.16.41-2 1.51-2 2.82 0 1.66 1.34 3 3 3s3-1.34 3-3c0-1.3-.84-2.4-2-2.82z';
const CLOCK_PATH =
  'M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z';

function Icon({ path }: { path: string }) {
  return (
    <svg
      className={styles.routeStatIcon}
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

export function RouteStats({
  distanceKm,
  durationMinutes,
  isRefining,
  isEstimated = false,
}: {
  distanceKm: number | null | undefined;
  durationMinutes: number | null | undefined;
  isRefining: boolean;
  isEstimated?: boolean;
}) {
  const { t } = useTranslation();
  const distance = formatDistance(distanceKm);
  if (distance == null) return null;
  const duration = formatDuration(durationMinutes);

  return (
    <div
      className={styles.routeStats}
      data-testid="route-distance-pill"
      role="group"
      aria-label={t('shipments.routeStats.ariaLabel', {
        approx: isEstimated ? t('shipments.routeStats.ariaApprox') : '',
        distance,
        duration: duration ?? t('shipments.routeStats.ariaUnavailable'),
      })}
    >
      <div className={styles.routeStat}>
        <Icon path={ROUTE_PATH} />
        <div className={styles.routeStatText}>
          {/* row-reverse: the marker renders after the value */}
          <ApproxMarker estimated={isEstimated} />
          <span className={styles.routeStatValue} data-testid="route-distance-value">
            {distance}
          </span>
          <span className={styles.routeStatLabel}>{t('shipments.routeStats.distance')}</span>
        </div>
      </div>
      <div className={styles.routeStat}>
        <Icon path={CLOCK_PATH} />
        <div className={styles.routeStatText}>
          <span className={styles.routeStatValue} data-testid="route-duration-value">
            {duration ?? '—'}
          </span>
          <span className={styles.routeStatLabel}>{t('shipments.routeStats.driveTime')}</span>
        </div>
      </div>
      {isRefining && (
        <span className={styles.routeRefiningText} role="status" aria-live="polite">
          {t('shipments.routeStats.refining')}
        </span>
      )}
    </div>
  );
}
